import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from "react";
import { supabase } from "../lib/supabase";
import {
  identifyUser,
  resetUser as resetAnalyticsUser,
  trackEvent,
} from "@jumpbean/analytics";

/**
 * Auth error type matching Supabase AuthError interface
 */
export interface AuthError {
  message: string;
  status?: number;
}

/**
 * Minimal user type for auth state
 */
export interface AuthUser {
  id: string;
  email?: string;
  created_at?: string;
}

/**
 * Auth context interface following the spec from issue #10
 */
export interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: AuthError | null;
  login: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  register: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  logout: () => Promise<void>;
  /** Alias for logout for backward compatibility */
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * AuthProvider - Provides centralized authentication state and methods.
 *
 * Wraps the app and provides:
 * - Current user state
 * - Authentication methods (login, register, logout, resetPassword)
 * - Loading and error states
 *
 * Uses Supabase Auth for all authentication operations.
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AuthError | null>(null);

  // Initialize auth state on mount
  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }

    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          created_at: session.user.created_at,
        });

        // Identify user in analytics on initial session load
        identifyUser(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });
      } else {
        setUser(null);
      }
      setIsLoading(false);
    });

    // Subscribe to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: string, session: { user: { id: string; email?: string; created_at?: string } } | null) => {
      if (session?.user) {
        setUser({
          id: session.user.id,
          email: session.user.email,
          created_at: session.user.created_at,
        });

        // Identify user in analytics
        identifyUser(session.user.id, {
          email: session.user.email,
          created_at: session.user.created_at,
        });

        // Track login event on sign-in (but not on initial session restore)
        if (event === "SIGNED_IN") {
          trackEvent("user_logged_in", { method: "email" });
        }
      } else {
        setUser(null);

        // Reset analytics user on sign out
        if (event === "SIGNED_OUT") {
          trackEvent("user_logged_out", {});
          resetAnalyticsUser();
        }
      }
      setIsLoading(false);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  /**
   * Login with email and password
   */
  const login = useCallback(
    async (email: string, password: string): Promise<{ error: AuthError | null }> => {
      if (!supabase) {
        const error: AuthError = { message: "Supabase client not configured" };
        setError(error);
        return { error };
      }

      setError(null);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const authError: AuthError = { message: error.message, status: error.status };
        setError(authError);
        return { error: authError };
      }

      return { error: null };
    },
    []
  );

  /**
   * Register a new account with email and password
   */
  const register = useCallback(
    async (email: string, password: string): Promise<{ error: AuthError | null }> => {
      if (!supabase) {
        const error: AuthError = { message: "Supabase client not configured" };
        setError(error);
        return { error };
      }

      setError(null);
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });

      if (error) {
        const authError: AuthError = { message: error.message, status: error.status };
        setError(authError);
        return { error: authError };
      }

      // Track signup event
      trackEvent("user_signed_up", { method: "email" });

      return { error: null };
    },
    []
  );

  /**
   * Logout the current user
   */
  const logout = useCallback(async () => {
    if (supabase) {
      await supabase.auth.signOut();
    }
    setError(null);
  }, []);

  /**
   * Send password reset email
   */
  const resetPassword = useCallback(
    async (email: string): Promise<{ error: AuthError | null }> => {
      if (!supabase) {
        const error: AuthError = { message: "Supabase client not configured" };
        setError(error);
        return { error };
      }

      setError(null);
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        const authError: AuthError = { message: error.message, status: error.status };
        setError(authError);
        return { error: authError };
      }

      return { error: null };
    },
    []
  );

  /**
   * Clear any auth errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: AuthContextValue = {
    user,
    isLoading,
    isAuthenticated: !!user,
    error,
    login,
    register,
    logout,
    signOut: logout, // Alias for backward compatibility
    resetPassword,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to access auth context.
 * Must be used within an AuthProvider.
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

export default AuthContext;
